
import React, { useState } from 'react';
import { DateRangePicker } from './analytics/DateRangePicker';
import { TokenUsageChart } from './analytics/TokenUsageChart';
import { ResponseTimeChart } from './analytics/ResponseTimeChart';
import { KnowledgeEffectivenessTable } from './analytics/KnowledgeEffectivenessTable';
import { StatsCards } from './analytics/StatsCards';
import { TopicFilterSelect } from './analytics/TopicFilterSelect';
import { FeedbackAnalyticsTab } from './analytics/FeedbackAnalyticsTab';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAnalytics } from '@/hooks/use-analytics';
import { addDays } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { TopicType } from '@/types/assistant';

export const AnalyticsDashboard = () => {
  // Set default date range to last 30 days
  const [dateRange, setDateRange] = useState<DateRange>({
    from: addDays(new Date(), -30),
    to: new Date()
  });
  
  // Set default topic filter to null (all topics)
  const [topicFilter, setTopicFilter] = useState<string | undefined>(undefined);
  
  // Set default tab
  const [activeTab, setActiveTab] = useState('token-usage');
  
  // Function to handle date range changes
  const handleDateRangeChange = (range: DateRange) => {
    setDateRange({
      from: range.from || dateRange.from,
      to: range.to || range.from || dateRange.to
    });
  };
  
  // Fetch analytics data based on filters
  const { 
    tokenUsageData, 
    responseTimeData, 
    knowledgeEffectivenessData,
    summaryStats,
    isLoading 
  } = useAnalytics({
    startDate: dateRange.from as Date,
    endDate: dateRange.to as Date,
    topic: topicFilter as TopicType
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h2 className="text-xl font-bold">Analytics Dashboard</h2>
        <div className="flex items-center gap-3">
          <TopicFilterSelect
            value={topicFilter}
            onChange={setTopicFilter}
          />
          <DateRangePicker 
            dateRange={dateRange} 
            onDateRangeChange={handleDateRangeChange} 
          />
        </div>
      </div>
      
      <StatsCards stats={summaryStats} isLoading={isLoading} />
      
      <Tabs 
        defaultValue="token-usage" 
        value={activeTab} 
        onValueChange={setActiveTab} 
        className="w-full"
      >
        <TabsList className="mb-2">
          <TabsTrigger value="token-usage">Token Usage</TabsTrigger>
          <TabsTrigger value="response-time">Response Time</TabsTrigger>
          <TabsTrigger value="knowledge-effectiveness">Knowledge Effectiveness</TabsTrigger>
          <TabsTrigger value="feedback-analysis">Feedback Analysis</TabsTrigger>
        </TabsList>
        
        <TabsContent value="token-usage" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Token Usage Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              <TokenUsageChart 
                data={tokenUsageData} 
                isLoading={isLoading} 
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="response-time" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Response Time Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponseTimeChart 
                data={responseTimeData} 
                isLoading={isLoading} 
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="knowledge-effectiveness" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Knowledge Base Effectiveness</CardTitle>
            </CardHeader>
            <CardContent>
              <KnowledgeEffectivenessTable 
                data={knowledgeEffectivenessData} 
                isLoading={isLoading} 
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="feedback-analysis" className="space-y-6">
          <FeedbackAnalyticsTab 
            startDate={dateRange.from as Date}
            endDate={dateRange.to as Date}
            topic={topicFilter}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
